import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { storiesApi, playersApi } from '../api/client';
import StoryCard from '../components/StoryCard';
import PlayerCard from '../components/PlayerCard';
import SEO, { SITE_URL } from '../components/SEO';

const CATEGORIES = [
  { label: 'Personal', type: 'personal', emoji: '✍️' },
  { label: 'Investigation', type: 'investigation', emoji: '🔍' },
  { label: 'Talent', type: 'talent', emoji: '⭐' },
  { label: 'Welfare', type: 'welfare', emoji: '🤝' },
  { label: 'Interview', type: 'interview', emoji: '🎙️' },
];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [stories, setStories] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      storiesApi.list({ featured: 'true' }),
      storiesApi.list({ ordering: '-created_at' }),
      playersApi.list({ featured: 'true' }),
    ]).then(([featRes, latestRes, playersRes]) => {
      setFeatured(featRes.data.results || featRes.data);
      setStories((latestRes.data.results || latestRes.data).slice(0, 8));
      setPlayers((playersRes.data.results || playersRes.data).slice(0, 4));
    }).finally(() => setLoading(false));
  }, []);

  const homeJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Pitch & Pen',
    url: SITE_URL,
    description: 'The platform where African football stories are told honestly.',
    publisher: {
      '@type': 'Organization',
      name: 'Pitch & Pen',
      url: SITE_URL,
      founder: { '@type': 'Organization', name: 'MistNex Dynamics', url: 'https://mistnex.co.zw' },
    },
    potentialAction: {
      '@type': 'SearchAction',
      target: { '@type': 'EntryPoint', urlTemplate: `${SITE_URL}/stories?search={search_term}` },
      'query-input': 'required name=search_term',
    },
  };

  return (
    <div>
      <SEO
        title="African Football Stories, Untold"
        description="Players forgotten after injuries. Talents lost to poverty. Careers built and destroyed in silence. Pitch & Pen gives African football a voice."
        url={SITE_URL}
        jsonLd={homeJsonLd}
      />
      {/* ── Hero ──────────────────────────────── */}
      <section className="hero">
        <div className="container">
          <div style={{ maxWidth: 640 }}>
            <p className="hero-tag">African Football Stories</p>
            <h1 className="hero-title">
              The stories African football never tells.
            </h1>
            <p className="hero-sub" style={{ marginBottom: '2rem' }}>
              Players forgotten after injuries. Talents lost to poverty. Careers built and destroyed in silence. We give the pitch a pen.
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <Link to="/stories" className="btn btn-lg" style={{ background: 'white', color: 'var(--green)', fontFamily: 'var(--font-sans)' }}>
                Read stories
              </Link>
              <Link to="/register" className="btn btn-lg btn-outline" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', fontFamily: 'var(--font-sans)' }}>
                Share your story
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── Category pills ─────────────────────── */}
      <div style={{ background: 'white', borderBottom: '1px solid var(--border)', padding: '0.75rem 0' }}>
        <div className="container">
          <div style={{ display: 'flex', gap: '0.5rem', overflowX: 'auto', paddingBottom: '2px' }}>
            {CATEGORIES.map(c => (
              <Link key={c.type} to={`/stories?type=${c.type}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
                  padding: '0.35rem 0.9rem', borderRadius: 20, border: '1px solid var(--border)',
                  fontFamily: 'var(--font-sans)', fontSize: '0.82rem', fontWeight: 500,
                  color: 'var(--text-muted)', background: 'white', whiteSpace: 'nowrap',
                  transition: 'all 0.12s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'var(--green-light)'; e.currentTarget.style.color = 'var(--green)'; e.currentTarget.style.borderColor = 'var(--green)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
              >
                <span>{c.emoji}</span> {c.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* ── Featured stories ────────────────────── */}
      {!loading && featured.length > 0 && (
        <section style={{ padding: '3rem 0', borderBottom: '1px solid var(--border)' }}>
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Featured</h2>
              <Link to="/stories?featured=true" className="section-link">All featured →</Link>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: featured.length === 1 ? '1fr' : 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '1.25rem',
            }}>
              {featured.slice(0, 3).map(s => <StoryCard key={s.id} story={s} featured />)}
            </div>
          </div>
        </section>
      )}

      {/* ── Latest stories magazine grid ────────── */}
      <section style={{ padding: '3rem 0', borderBottom: '1px solid var(--border)' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Latest stories</h2>
            <Link to="/stories" className="section-link">View all →</Link>
          </div>
          {loading ? (
            <div className="loading-text">Loading stories…</div>
          ) : stories.length === 0 ? (
            <div className="empty-state">
              <h3>No stories yet</h3>
              <p>Be the first to write one.</p>
              <Link to="/write" className="btn btn-primary" style={{ marginTop: '1rem' }}>Write a story</Link>
            </div>
          ) : (
            <div className="magazine-grid">
              {stories.map(s => <StoryCard key={s.id} story={s} />)}
            </div>
          )}
        </div>
      </section>

      {/* ── Player spotlight ──────────────────────── */}
      {!loading && players.length > 0 && (
        <section style={{ padding: '3rem 0', borderBottom: '1px solid var(--border)' }}>
          <div className="container">
            <div className="section-header">
              <h2 className="section-title">Player spotlight</h2>
              <Link to="/players" className="section-link">Talent directory →</Link>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '1.25rem' }}>
              {players.map(p => <PlayerCard key={p.id} player={p} />)}
            </div>
          </div>
        </section>
      )}

      {/* ── CTA ────────────────────────────────────── */}
      <section style={{ padding: '4rem 0' }}>
        <div className="container">
          <div style={{
            background: 'linear-gradient(135deg, var(--green-dark) 0%, var(--green) 100%)',
            borderRadius: 'var(--radius-lg)', padding: '3rem 2.5rem', color: 'white',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: '1.5rem',
          }}>
            <div>
              <h2 style={{ fontSize: '1.6rem', marginBottom: '0.5rem' }}>Your voice matters.</h2>
              <p style={{ opacity: 0.82, lineHeight: 1.65, maxWidth: 480 }}>
                Whether you're a player, journalist, scout or fan — African football needs your story. Share it here.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <Link to="/write" className="btn btn-lg" style={{ background: 'white', color: 'var(--green)', fontFamily: 'var(--font-sans)' }}>
                ✏️ Write a story
              </Link>
              <Link to="/register" className="btn btn-lg btn-outline" style={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', fontFamily: 'var(--font-sans)' }}>
                Join the community
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
